// Audio Integrity and Quality Assessment Utilities
// PRIORITY: Capture everything, analyze quality for optimization

export interface AudioIntegrityReport {
  // Core integrity metrics
  isValid: boolean;
  hasContent: boolean;
  duration: number;
  fileSize: number;
  
  // Quality assessment
  quality: 'excellent' | 'good' | 'fair' | 'poor' | 'very-poor';
  maxAmplitude: number;
  avgAmplitude: number;
  rmsLevel: number;
  nonSilentPercentage: number;
  
  // Enhancement recommendations
  recommendations: {
    needsAmplification: boolean;
    needsNoiseReduction: boolean;
    needsNormalization: boolean;
    transcriptionDifficulty: 'low' | 'medium' | 'high' | 'very-high';
  };
  
  // Metadata for optimization
  metadata: {
    sampleRate: number;
    channels: number;
    bitDepth: number;
    format: string;
    compressionRatio?: number;
  };
  
  // Failure details (if any)
  issues: string[];
}

/**
 * BULLETPROOF audio integrity check - never fails, always provides report
 */
export async function analyzeAudioIntegrity(audioBlob: Blob, recordingTime: number): Promise<AudioIntegrityReport> {
  const report: AudioIntegrityReport = {
    isValid: false,
    hasContent: false,
    duration: recordingTime,
    fileSize: audioBlob.size,
    quality: 'very-poor',
    maxAmplitude: 0,
    avgAmplitude: 0,
    rmsLevel: 0,
    nonSilentPercentage: 0,
    recommendations: {
      needsAmplification: false,
      needsNoiseReduction: false,
      needsNormalization: false,
      transcriptionDifficulty: 'very-high'
    },
    metadata: {
      sampleRate: 0,
      channels: 0,
      bitDepth: 16, // assumption
      format: audioBlob.type,
    },
    issues: []
  };

  try {
    // Basic file integrity checks
    if (audioBlob.size === 0) {
      report.issues.push('Empty audio file');
      return report;
    }
    
    if (audioBlob.size < 100) {
      report.issues.push('Suspiciously small file size');
    }
    
    report.hasContent = true;
    
    // Attempt audio analysis
    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Try to decode audio
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Basic metadata
      report.metadata.sampleRate = audioBuffer.sampleRate;
      report.metadata.channels = audioBuffer.numberOfChannels;
      report.duration = audioBuffer.duration;
      report.isValid = true;
      
      // Audio quality analysis — single-pass loop to avoid stack overflow
      // (Math.max(...array) exceeds call stack for large sample arrays)
      const channelData = audioBuffer.getChannelData(0);
      const length = channelData.length;
      let maxAmp = 0;
      let sumAmp = 0;
      let sumSquared = 0;
      let nonSilentCount = 0;
      const silenceThreshold = 0.001;

      for (let i = 0; i < length; i++) {
        const abs = Math.abs(channelData[i]);
        if (abs > maxAmp) maxAmp = abs;
        sumAmp += abs;
        sumSquared += channelData[i] * channelData[i];
        if (abs > silenceThreshold) nonSilentCount++;
      }

      report.maxAmplitude = maxAmp;
      report.avgAmplitude = sumAmp / length;
      report.rmsLevel = Math.sqrt(sumSquared / length);
      report.nonSilentPercentage = (nonSilentCount / length) * 100;
      
      // Quality assessment
      if (report.maxAmplitude > 0.1) {
        report.quality = 'excellent';
      } else if (report.maxAmplitude > 0.05) {
        report.quality = 'good';
      } else if (report.maxAmplitude > 0.01) {
        report.quality = 'fair';
      } else if (report.maxAmplitude > 0.001) {
        report.quality = 'poor';
      } else {
        report.quality = 'very-poor';
      }
      
      // Enhancement recommendations
      report.recommendations.needsAmplification = report.maxAmplitude < 0.05;
      report.recommendations.needsNoiseReduction = report.rmsLevel > 0.01 && report.maxAmplitude < 0.02;
      report.recommendations.needsNormalization = report.avgAmplitude < 0.001;
      
      // Transcription difficulty assessment
      if (report.quality === 'excellent' || report.quality === 'good') {
        report.recommendations.transcriptionDifficulty = 'low';
      } else if (report.quality === 'fair') {
        report.recommendations.transcriptionDifficulty = 'medium';
      } else if (report.quality === 'poor') {
        report.recommendations.transcriptionDifficulty = 'high';
      } else {
        report.recommendations.transcriptionDifficulty = 'very-high';
      }
      
      // Additional quality checks
      if (report.nonSilentPercentage < 5) {
        report.issues.push('Recording contains mostly silence');
      }
      
      if (report.duration < 1) {
        report.issues.push('Very short recording duration');
      }
      
      if (audioBuffer.sampleRate < 8000) {
        report.issues.push('Low sample rate may affect transcription quality');
      }
      
      await audioContext.close();
      
    } catch (decodeError) {
      console.error('Audio decode failed but file preserved:', decodeError);
      report.issues.push(`Audio decode failed: ${decodeError instanceof Error ? decodeError.message : 'Unknown error'}`);
      report.recommendations.transcriptionDifficulty = 'very-high';
      
      // Still mark as having content if file has reasonable size
      if (audioBlob.size > 1000) {
        report.hasContent = true;
      }
    }
    
  } catch (error) {
    console.error('Audio integrity analysis failed but audio preserved:', error);
    report.issues.push(`Analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    
    // Even if analysis completely fails, preserve the audio
    report.hasContent = audioBlob.size > 0;
  }
  
  return report;
}

/**
 * Generate human-readable quality report
 */
export function generateQualityReport(report: AudioIntegrityReport): string {
  const lines: string[] = [];
  
  lines.push(`📊 Audio Quality: ${report.quality.toUpperCase()}`);
  lines.push(`📏 Duration: ${report.duration.toFixed(1)}s`);
  lines.push(`📦 Size: ${(report.fileSize / 1024).toFixed(1)} KB`);
  
  if (report.isValid) {
    lines.push(`🎵 Sample Rate: ${report.metadata.sampleRate} Hz`);
    lines.push(`🔊 Max Volume: ${(report.maxAmplitude * 100).toFixed(1)}%`);
    lines.push(`📈 Speech Content: ${report.nonSilentPercentage.toFixed(1)}%`);
  }
  
  if (report.recommendations.transcriptionDifficulty !== 'low') {
    lines.push(`⚠️  Transcription Difficulty: ${report.recommendations.transcriptionDifficulty.toUpperCase()}`);
  }
  
  if (report.issues.length > 0) {
    lines.push('🔍 Issues detected:');
    report.issues.forEach(issue => lines.push(`   • ${issue}`));
  }
  
  return lines.join('\n');
}

/**
 * Get optimization parameters for transcription service
 */
export function getOptimizationParameters(report: AudioIntegrityReport) {
  return {
    // Audio preprocessing recommendations
    amplify: report.recommendations.needsAmplification,
    noiseReduce: report.recommendations.needsNoiseReduction,
    normalize: report.recommendations.needsNormalization,
    
    // Transcription service parameters
    sensitivity: report.quality === 'poor' || report.quality === 'very-poor' ? 'high' : 'normal',
    language: 'en-US', // Could be made configurable
    model: report.quality === 'excellent' || report.quality === 'good' ? 'standard' : 'enhanced',
    
    // Quality metadata
    expectedDifficulty: report.recommendations.transcriptionDifficulty,
    originalQuality: report.quality,
    fileIntegrity: report.isValid ? 'valid' : 'degraded'
  };
}